import React, { useMemo, useState } from 'react';
import { Message, Placeholder } from 'rsuite';
import { Button, ConfirmModal, Modal } from '@nsi-advisory-services/components';
import PermissionGate from '../shared/PermissionGate';
import { useAuth } from '../../contexts/AuthContext';
import {
  useCloseDates,
  useCreateCloseDate,
  useDeleteCloseDate,
  useUpdateCloseDate,
} from '../../hooks/useQueries';
import CloseDateForm from './CloseDateForm';

const nthLabel = (value) => {
  const n = Number(value);
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  if (n === 4) return '4th';
  if (n === 5) return '5th';
  return `${n}`;
};

const monthName = (month) => {
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return names[Number(month) - 1] || '';
};

const weekdayName = (weekday) => {
  const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return names[Number(weekday)] || '';
};

const recurrenceDescription = (row) => {
  const recurrenceType = String(row?.recurrence_type || '').toLowerCase();
  const m = monthName(row?.month);

  if (recurrenceType === 'fixed') {
    return `${m} ${row?.day}`;
  }

  if (recurrenceType === 'nth_weekday') {
    return `${nthLabel(row?.n)} ${weekdayName(row?.weekday)} of ${m}`;
  }

  if (recurrenceType === 'first_weekday') {
    return `First ${weekdayName(row?.weekday)} of ${m}`;
  }

  if (recurrenceType === 'last_weekday') {
    return `Last ${weekdayName(row?.weekday)} of ${m}`;
  }

  if (recurrenceType === 'nth_week') {
    return `${nthLabel(row?.n)} week of ${m}${row?.duration_days ? ` (${row.duration_days} days)` : ''}`;
  }

  return '';
};

const extractYearRange = (row, year) => {
  const found = (row?.resolved_dates || []).find((item) => Number(item.year) === Number(year));
  if (!found?.startDate) return '—';
  if (!found?.endDate || found.endDate === found.startDate) return found.startDate;
  return `${found.startDate} → ${found.endDate}`;
};

export default function CloseDatesSection() {
  const auth = useAuth();
  const showAdminActions = auth.hasPermission('manage_all_employees');

  const query = useCloseDates({ enabled: true });
  const createMutation = useCreateCloseDate();
  const updateMutation = useUpdateCloseDate();
  const deleteMutation = useDeleteCloseDate();

  const [editTarget, setEditTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const nowYear = new Date().getFullYear();

  const rows = useMemo(() => {
    const data = query.data || [];
    return [...data].sort((a, b) => {
      const aThisYear = (a?.resolved_dates || []).find((item) => Number(item.year) === Number(nowYear));
      const bThisYear = (b?.resolved_dates || []).find((item) => Number(item.year) === Number(nowYear));

      const aDate = aThisYear?.startDate || '';
      const bDate = bThisYear?.startDate || '';

      if (aDate && bDate) return aDate.localeCompare(bDate);
      if (aDate) return -1;
      if (bDate) return 1;
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [nowYear, query.data]);

  const createError = createMutation.error;
  const updateError = updateMutation.error;

  const openCreate = () => setCreateOpen(true);
  const closeCreate = () => setCreateOpen(false);

  const openEdit = (row) => {
    setEditTarget(row);
    setEditOpen(true);
  };
  const closeEdit = () => setEditOpen(false);

  const openDelete = (row) => setDeleteTarget(row);
  const closeDelete = () => setDeleteTarget(null);

  const handleCreate = async (payload) => {
    await createMutation.mutateAsync(payload);
    closeCreate();
  };

  const handleUpdate = async (payload) => {
    if (!editTarget?.id) return;
    await updateMutation.mutateAsync({ id: editTarget.id, ...payload });
    closeEdit();
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
    closeDelete();
    closeEdit();
  };

  if (query.isLoading) {
    return <Placeholder.Paragraph rows={5} active />;
  }

  if (query.error) {
    return (
      <Message type="error" showIcon header="Error">
        {String(query.error?.message || query.error)}
      </Message>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">Close Dates</h3>
            <span className="bdc-count">{rows.length}</span>
          </div>

          <PermissionGate permission="manage_all_employees">
            <div className="bdc-table-head__actions">
              <button type="button" className="bdc-btn bdc-btn--sm bdc-btn--primary" onClick={openCreate}>
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Add Close Date
              </button>
            </div>
          </PermissionGate>
        </div>

        <table className="nsi-table">
          <thead>
            <tr>
              <th style={{ width: 210 }}>Name</th>
              <th>Recurrence Description</th>
              <th style={{ width: 210 }}>This Year</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="nsi-table__empty">No close dates yet.</td>
              </tr>
            ) : rows.map((row) => (
              <tr
                key={row.id}
                className={showAdminActions ? 'nsi-table__row' : undefined}
                onClick={showAdminActions ? () => openEdit(row) : undefined}
                onKeyDown={showAdminActions ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openEdit(row);
                  }
                } : undefined}
                tabIndex={showAdminActions ? 0 : undefined}
                role={showAdminActions ? 'button' : undefined}
                title={showAdminActions ? 'Edit this close date' : undefined}
              >
                <td>{row.name}</td>
                <td className="bdc-table-wrap">{recurrenceDescription(row)}</td>
                <td className="bdc-num">{extractYearRange(row, nowYear)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} onClose={closeCreate} size="sm">
        <Modal.Header>
          <Modal.Title>Add Close Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CloseDateForm
            onSubmit={handleCreate}
            loading={createMutation.isPending}
            error={createError}
          />
        </Modal.Body>
      </Modal>

      <Modal open={editOpen} onClose={closeEdit} onExited={() => setEditTarget(null)} size="sm">
        <Modal.Header>
          <Modal.Title>Edit Close Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CloseDateForm
            initialValues={editTarget || {}}
            onSubmit={handleUpdate}
            loading={updateMutation.isPending}
            error={updateError}
          />
        </Modal.Body>
        <Modal.Footer
          left={
            <Button variant="danger" icon="trash" onClick={() => openDelete(editTarget)}>
              Delete
            </Button>
          }
        >
          <Button onClick={closeEdit}>Close</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Close Date"
        body={`Delete ${deleteTarget?.name ? `"${deleteTarget.name}"` : 'this close date'}?`}
        confirmText="Delete"
        danger
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={closeDelete}
      />
    </div>
  );
}
